import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { PlusCircle, Loader2, Pencil, X, Check, Trash2 } from 'lucide-react';
import { isProjectOwner } from '../../lib/permissions';

interface CustomField {
  id: string;
  name: string;
  field_type: string;
  options?: string[];
  created_at: string;
}

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  fieldName: string;
}

function DeleteModal({ isOpen, onClose, onConfirm, fieldName }: DeleteModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Delete Custom Field
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete the custom field "{fieldName}"? This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex w-full justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 inline-flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CustomFieldsSection({ projectId }: { projectId: string }) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingField, setIsAddingField] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<CustomField | null>(null);
  const [newField, setNewField] = useState({
    name: '',
    field_type: 'text',
    options: ''
  });

  useEffect(() => {
    fetchFields();
    checkPermissions();
  }, [projectId]);

  async function checkPermissions() {
    const isOwner = await isProjectOwner(projectId);
    setCanEdit(isOwner);
  }

  async function fetchFields() {
    try {
      const { data, error } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddField(e: React.FormEvent) {
    e.preventDefault();
    try {
      const fieldData = {
        project_id: projectId,
        name: newField.name,
        field_type: newField.field_type,
        options: ['single_select', 'multi_select'].includes(newField.field_type) 
          ? newField.options.split(',').map(opt => opt.trim())
          : null
      };

      const { data, error } = await supabase
        .from('custom_fields')
        .insert(fieldData)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setFields([...fields, data]);
        setIsAddingField(false);
        setNewField({ name: '', field_type: 'text', options: '' });
      }
    } catch (error: any) {
      setError(error.message);
    }
  }

  async function handleUpdateField(field: CustomField) {
    try {
      const fieldData = {
        name: field.name,
        field_type: field.field_type,
        options: ['single_select', 'multi_select'].includes(field.field_type) 
          ? field.options
          : null
      };

      const { error } = await supabase
        .from('custom_fields')
        .update(fieldData)
        .eq('id', field.id);

      if (error) throw error;
      
      setFields(fields.map(f => f.id === field.id ? { ...f, ...fieldData } : f));
      setEditingField(null);
    } catch (error: any) {
      setError(error.message);
    }
  }

  async function handleDeleteField() {
    if (!fieldToDelete) return;

    try {
      const { error } = await supabase
        .from('custom_fields')
        .delete()
        .eq('id', fieldToDelete.id);

      if (error) throw error;
      
      setFields(fields.filter(f => f.id !== fieldToDelete.id));
      setDeleteModalOpen(false);
      setFieldToDelete(null);
    } catch (error: any) {
      setError(error.message);
    }
  }

  const showOptionsInput = ['single_select', 'multi_select'].includes(newField.field_type);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-base font-semibold leading-6 text-brand-teal">Custom Fields</h2>
          <p className="mt-2 text-sm text-gray-700">
            Define custom fields to collect during interviews
          </p>
        </div>
        {canEdit && (
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              onClick={() => setIsAddingField(true)}
              className="inline-flex items-center rounded-lg bg-brand-orange px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Field
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddingField && canEdit && (
        <form onSubmit={handleAddField} className="mt-6 space-y-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label htmlFor="fieldName" className="block text-sm font-medium text-gray-700">
              Field Name
            </label>
            <input
              type="text"
              id="fieldName"
              required
              value={newField.name}
              onChange={(e) => setNewField({ ...newField, name: e.target.value })}
              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
            />
          </div>

          <div>
            <label htmlFor="fieldType" className="block text-sm font-medium text-gray-700">
              Field Type
            </label>
            <select
              id="fieldType"
              value={newField.field_type}
              onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="boolean">Yes/No</option>
              <option value="date">Date</option>
              <option value="single_select">Single Select</option>
              <option value="multi_select">Multi Select</option>
            </select>
          </div>

          {showOptionsInput && (
            <div>
              <label htmlFor="options" className="block text-sm font-medium text-gray-700">
                Options (comma-separated)
              </label>
              <input
                type="text"
                id="options"
                required
                value={newField.options}
                onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                placeholder="Option 1, Option 2, Option 3"
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
              />
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setIsAddingField(false);
                setNewField({ name: '', field_type: 'text', options: '' });
              }}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center rounded-lg border border-transparent bg-brand-orange px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
            >
              Add Field
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-brand-teal sm:pl-6">
                      Field Name
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-brand-teal">
                      Type
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-brand-teal">
                      Options
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-brand-teal">
                      Created
                    </th>
                    {canEdit && (
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-brand-teal pr-6">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {fields.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 5 : 4} className="py-8 text-center text-sm text-gray-500">
                        No custom fields defined yet. {canEdit && 'Add your first field to get started!'}
                      </td>
                    </tr>
                  ) : (
                    fields.map((field) => (
                      <tr key={field.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {editingField === field.id ? (
                            <input
                              type="text"
                              value={field.name}
                              onChange={(e) => setFields(fields.map(f => 
                                f.id === field.id ? { ...f, name: e.target.value } : f
                              ))}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                            />
                          ) : (
                            field.name
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {field.field_type}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {editingField === field.id && ['single_select', 'multi_select'].includes(field.field_type) ? (
                            <input
                              type="text"
                              value={field.options?.join(', ') || ''}
                              onChange={(e) => setFields(fields.map(f => 
                                f.id === field.id ? { ...f, options: e.target.value.split(',').map(opt => opt.trim()) } : f
                              ))}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                              placeholder="Option 1, Option 2, Option 3"
                            />
                          ) : (
                            field.options ? field.options.join(', ') : '-'
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(field.created_at).toLocaleDateString()}
                        </td>
                        {canEdit && (
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right pr-6">
                            {editingField === field.id ? (
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleUpdateField(field)}
                                  className="text-green-600 hover:text-green-900 transition-colors"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setFields(fields.map(f => f.id === field.id ? { ...field } : f));
                                    setEditingField(null);
                                  }}
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => setEditingField(field.id)}
                                  className="text-brand-orange hover:text-brand-orange-light transition-colors"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setFieldToDelete(field);
                                    setDeleteModalOpen(true);
                                  }}
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setFieldToDelete(null);
        }}
        onConfirm={handleDeleteField}
        fieldName={fieldToDelete?.name || ''}
      />
    </div>
  );
}